import { ChangeEventHandler, FocusEventHandler, useCallback, useRef } from 'react';

import { IInputProps, Omit } from '@rbi-ctg/frontend';

interface IUseInputHandlesProps<I = HTMLInputElement> extends Omit<IInputProps<I>, 'value'> {
  inputId: string;
  isMobile: boolean;
  queryType?: string;
}

interface IInputHandles<I = HTMLInputElement> {
  handleBlur: FocusEventHandler<I>;
  handleChange: ChangeEventHandler<I>;
  handleFocus: FocusEventHandler<I>;
}

export default function useInputHandles<I = HTMLInputElement>({
  inputId,
  isMobile,
  queryType = 'input',
  onBlur,
  onChange,
  onFocus
}: IUseInputHandlesProps<I>): IInputHandles<I> {
  const needsAutocompleteFocus = useRef(false);

  const handleChange = useCallback<ChangeEventHandler<I>>(
    e => {
      setTimeout(() => {
        try {
          const isAutoCompleted = !!document.querySelector(
            `${queryType}#${inputId}:-webkit-autofill`
          );

          if (isAutoCompleted) {
            needsAutocompleteFocus.current = true;
          }
        } catch (err) {
          // Do nothing
        }
      }, 1);

      if (onChange) {
        onChange(e);
      }
    },
    [inputId, onChange, queryType]
  );

  const handleBlur = useCallback<FocusEventHandler<I>>(
    e => {
      if (onBlur) {
        onBlur(e);
      }

      if (isMobile) {
        try {
          const autoCompleteNode = document.querySelector(
            `${queryType}#${inputId}:-webkit-autofill`
          ) as HTMLInputElement;

          if (needsAutocompleteFocus.current) {
            needsAutocompleteFocus.current = false;
            autoCompleteNode && autoCompleteNode.focus();
          }

          return;
        } catch (error) {
          // Do nothing
        }
      }
    },
    [inputId, isMobile, onBlur, queryType]
  );

  const handleFocus = useCallback<FocusEventHandler<I>>(
    event => {
      if (onFocus) {
        onFocus(event);
      }
    },
    [onFocus]
  );

  return {
    handleBlur,
    handleChange,
    handleFocus
  };
}
